import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import moment from "moment";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import { optimumFrictionConstants } from "../../../../../_redux/_constants/optimumFriction.constants";
import {
     optimumFrictionGetReportRequest,
     participantGetAreasRequest,
     participantGetDealersRequest,
} from "../../../../../_redux/_actions";
import OptimumFrictionReportSummary, {
     money,
} from "./OptimumFrictionReportSummary";

const ALL_OPTION = { id: null, name: "All" };

const INITIAL_FILTERS = {
     area: ALL_OPTION,
     dealer: ALL_OPTION,
};

const INITIAL_PAGE = { skip: 0, take: 25 };

// Optimum Friction is an Equipe campaign, so it reads the network the rest of the portal reads
// rather than keeping an area and dealer list of its own.
const EQUIPE_APPLICATION_ID = 1;

const asDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "");

// The winners as Mike reads them, in this order and under these headings.
const EXPORT_COLUMNS = [
     ["Name", (row) => row.name || ""],
     ["Email", (row) => row.email || ""],
     ["Dealer Code", (row) => row.dealerCode || ""],
     ["Dealership", (row) => row.dealerName || ""],
     ["Won", (row) => row.valueGbp ?? ""],
     ["Points", (row) => row.pointsWon ?? ""],
     ["Card issued", (row) => asDate(row.issuedOn)],
     ["Played", (row) => asDate(row.playedOn)],
];

export default function OptimumFrictionReportGrid() {
     const actionDispatch = useDispatch();

     const report = useSelector((state) => state?.optimumFriction?.report);
     const areaList = useSelector(
          (state) => state?.participantsManagement?.areaList,
     );
     const dealerList = useSelector(
          (state) => state?.participantsManagement?.dealerList,
     );

     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               optimumFrictionConstants.OPTIMUM_FRICTION_GET_REPORT_REQUEST,
          ),
     );

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [page, setPage] = useState(INITIAL_PAGE);

     useEffect(() => {
          actionDispatch(
               participantGetAreasRequest({
                    applicationId: EQUIPE_APPLICATION_ID,
               }),
          );
          actionDispatch(
               participantGetDealersRequest({
                    applicationId: EQUIPE_APPLICATION_ID,
               }),
          );
     }, [actionDispatch]);

     // The criteria the report is built from. Held in one place so the figures, the list and the
     // export are always asked for exactly the same set of winners.
     const criteria = useMemo(
          () => ({
               areaId: filters.area.id || undefined,
               dealershipId: filters.dealer.id || undefined,
          }),
          [filters],
     );

     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    optimumFrictionGetReportRequest({
                         ...criteria,
                         page: Math.floor(page.skip / page.take) + 1,
                         pageSize: page.take,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, criteria, page]);

     const areaOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(Array.isArray(areaList) ? areaList : []).map((area) => ({
                    id: area.id,
                    name: area.area ?? area.name,
               })),
          ],
          [areaList],
     );

     const dealerOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(Array.isArray(dealerList) ? dealerList : []).map(
                    (dealer) => ({
                         id: dealer.id,
                         name: dealer.dealershipCode
                              ? `${dealer.dealershipCode} - ${dealer.name}`
                              : dealer.name,
                    }),
               ),
          ],
          [dealerList],
     );

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const clearFilters = useCallback(() => {
          setFilters(INITIAL_FILTERS);
          setPage(INITIAL_PAGE);
     }, []);

     const winners = useMemo(() => {
          const rows = report?.winners || [];

          return rows.map((row) => ({
               ...row,
               won: money(row.valueGbp),
               cardIssued: asDate(row.issuedOn),
               played: asDate(row.playedOn),
          }));
     }, [report]);

     const totalCount = report?.totalCount || 0;

     // The file holds the page on screen. The report is one page of winners at a time, so asking
     // for a second, unpaged copy would be a different set from the one the button sits under.
     const exportWinners = useCallback(() => {
          const sheetRows = (report?.winners || []).map((row) => {
               const line = {};

               EXPORT_COLUMNS.forEach(([heading, read]) => {
                    line[heading] = read(row);
               });

               return line;
          });

          const sheet = XLSX.utils.json_to_sheet(sheetRows, {
               header: EXPORT_COLUMNS.map(([heading]) => heading),
          });
          const workbook = XLSX.utils.book_new();

          XLSX.utils.book_append_sheet(workbook, sheet, "Winners");
          XLSX.writeFile(
               workbook,
               `optimum-friction-winners-${moment().format("YYYY-MM-DD")}.xlsx`,
          );
     }, [report]);

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         onChange={(event) =>
                              onFilterChange("area", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         onChange={(event) =>
                              onFilterChange("dealer", event.value)
                         }
                    />
                    <Button type="button" onClick={clearFilters}>
                         Clear
                    </Button>
                    <Button
                         type="button"
                         themeColor="primary"
                         disabled={isLoading || !winners.length}
                         onClick={exportWinners}
                    >
                         Export to Excel
                    </Button>
               </div>

               {/* The filter bar has no bottom margin of its own, so the tiles keep clear of
                   it here rather than sitting under an open dropdown. */}
               <div style={{ marginTop: "24px" }}>
                    <OptimumFrictionReportSummary report={report} />
               </div>

               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}

                    <Grid
                         data={winners}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         className="sba-area-data-grid"
                         pageable={{ buttonCount: 4, pageSizes: [25, 50, 100] }}
                         onPageChange={(event) => setPage(event.page)}
                    >
                         <GridColumn field="name" title="Name" width="200px" />
                         <GridColumn field="email" title="Email" width="260px" />
                         <GridColumn
                              field="dealerCode"
                              title="Dealer Code"
                              width="140px"
                         />
                         <GridColumn
                              field="dealerName"
                              title="Dealership"
                              width="240px"
                         />
                         <GridColumn field="won" title="Won" width="120px" />
                         <GridColumn
                              field="pointsWon"
                              title="Points"
                              width="120px"
                         />
                         <GridColumn
                              field="cardIssued"
                              title="Card issued"
                              width="150px"
                         />
                         <GridColumn
                              field="played"
                              title="Played"
                              width="150px"
                         />
                    </Grid>
               </div>
          </div>
     );
}
